import Pagination from './Pagination';
import File from './File';

export default class Result {
	constructor(domContainer, data, config) {
		try {
			this.data = data;
			// Create lookup for quick file checks
			const mapFileUidToIndex = {};
			for (let i = 0; i < this.data.files.length; ++i) {
				mapFileUidToIndex[this.data.files[i].uid] = i;
			}
			this.data.mapFileUidToIndex = mapFileUidToIndex;

			this.container = domContainer;
			this.config = {
					hiddenClass: 'hidden',
					...config
				};

			this.selectors = {
					fileItemSelector: config?.selectors?.fileItemSelector || '.js-dc-file-item',
					paginationSelector: config?.selectors?.paginationSelector || '.js-dc-pagination'
				};
			this.resultsCounter = document.querySelectorAll('.js-dc-result-counter');

			this.files = [];
			this.#collectFiles();
			this.filePool = this.files.slice(0);  // the total pool of possible to display files
			this.displayedFiles = this.filePool.slice(0, 8);  // the currently visible amount of files

			let domPaginationContainer = this.container.querySelectorAll(this.selectors.paginationSelector);
			if (domPaginationContainer.length > 0) {
				this.pagination = new Pagination(domPaginationContainer, { onChangePage: this.#onChangePage.bind(this) });
				this.pagination.createPagination(this.filePool.length);
			}
		} catch (e) {
			console.error(e);
		}
	}

	#onChangePage(newPage) {
		let offset = (newPage-1) * 8;
		if (offset >= this.files.length) {
			return;
		}

		this.displayedFiles.forEach(file => {
			file.setVisibility(false);
		});
		this.displayedFiles = this.filePool.slice(offset, offset+8);
		this.displayedFiles.forEach(file => {
			file.setVisibility(true);
		});

		this.pagination?.setPage(newPage, this.filePool.length);
	}

	/**
	 * Hides/Shows files based on the filter conditions.
	 * 
	 * @param {Object} conditions 
	 * @returns {Object} Object of the used categories inside the shown files, format: { catUid: true }
	 */
	show(conditions, bulletPoints) {
		let fileUids = bulletPoints !== undefined ? Object.keys(bulletPoints) : [];  // fileUids => keyword
		let groups = Object.keys(conditions);

		if (groups.length === 0) {
			if (fileUids.length > 0) {
				let matchedFiles = [];
				let mapShowFiles = {};

				fileUids.forEach(fileUid => {
					let keyword = bulletPoints[fileUid];

					this.files.forEach(file => {
						let fileUid = file.getUid();
	
						if (!mapShowFiles[fileUid]) {
							if (file.searchFile(keyword)) {
								mapShowFiles[fileUid] = true;
								matchedFiles.push(file);
							}
						}
					});
				});

				this.files.forEach((file) => {
					file.setVisibility(false);
				});

				// update internal file lists
				this.filePool = matchedFiles.slice(0);
				this.displayedFiles = this.filePool.slice(0, 8);
				this.displayedFiles.forEach(file => {
					file.setVisibility(true);
				});
				this.pagination.setPage(1, this.filePool.length);
				this.#updateResultCounter(matchedFiles.length);
			} else {
				this.files.forEach(file => file.setVisibility(false));

				this.filePool = this.files.slice(0);
				this.displayedFiles = this.filePool.slice(0, 8);
				this.displayedFiles.forEach(file => {
					file.setVisibility(true);
				});
				this.#updateResultCounter(this.filePool.length);
			}

			return [];
		} else {
			let matchedFiles = [];
			let mapAddedFile = {};

			conditions[groups[0]].forEach(cat => {
				let catUid = cat.getUid();
				this.files.forEach(file => {
					let fileUid = file.getUid();

					if (!mapAddedFile[fileUid]) {
						if (file.hasCategory(catUid)) {
							mapAddedFile[fileUid] = true;
							matchedFiles.push(file);
						}
					}
				});
			});

			// if more than one group is used, we need to check whether a file has at least one of the other groups categories set
			if (groups.length > 1) {
				groups.forEach(groupIndex => {
					if (groupIndex === 0) { return; }  // = initial matchedFiles

					let newMatchedFiles = [];
					mapAddedFile = {};  // reset map
					conditions[groupIndex].forEach(cat => {
						let catUid = cat.getUid();

						matchedFiles.forEach(file => {
							let fileUid = file.getUid();

							if (!mapAddedFile[fileUid]) {
								if (file.hasCategory(catUid)) {
									mapAddedFile[fileUid] = true;
									newMatchedFiles.push(file);
								}
							}
						});
					});

					matchedFiles = newMatchedFiles.slice(0);
				});
			}


			if (fileUids.length > 0) {
				let newMatchedFiles = [];
					mapAddedFile = {};  // reset map
				fileUids.forEach(fileUid => {
					let keyword = bulletPoints[fileUid];

					matchedFiles.forEach(file => {
						let fileUid = file.getUid();
	
						if (!mapAddedFile[fileUid]) {
							if (file.searchFile(keyword)) {
								mapAddedFile[fileUid] = true;
								newMatchedFiles.push(file);
							}
						}
					});
				});

				matchedFiles = newMatchedFiles.slice(0);
			}

			// Now show all files that are in the matchedFiles array, hide all others
			let mapShowFiles = matchedFiles.reduce((map, file) => {
					map[file.getUid()] = true;
					return map;
				}, {});
			let usedCategories = {};
			this.files.forEach((file, i) => {
				if (mapShowFiles[file.getUid()] === true) {
					if (i < 8) {
						file.setVisibility(true);
					}

					file.getCategories().forEach(cat => {
						usedCategories[cat.uid] = true;
						if (cat.l10n_parent > 0) {
							usedCategories[cat.l10n_parent] = true;
						}
					});
				} else {
					file.setVisibility(false);
				}
			});
			this.#updateResultCounter(matchedFiles.length);

			// update internal file lists
			this.filePool = matchedFiles.slice(0);
			this.displayedFiles = this.filePool.slice(0, 8);
			this.displayedFiles.forEach(file => {
				file.setVisibility(true);
			});
			this.pagination.setPage(1, this.filePool.length);

			return usedCategories;
		}
	}

	#updateResultCounter(count) {
		this.resultsCounter.forEach(counter => {
			counter.innerHTML = count;
		});
	}

	/**
	 * Unhides all files. Used when filter is reset.
	 */
	showAll() {
		// hide all currently displayed
		this.displayedFiles.forEach(file => {
			file.setVisibility(false);
		});

		// Reset display to initial state
		this.filePool = this.files.slice(0);
		this.displayedFiles = this.filePool.slice(0, 8);
		this.displayedFiles.forEach(file => {
			file.setVisibility(true);
		});
		this.pagination?.setPage(1, this.filePool.length);

		this.#updateResultCounter(this.filePool.length);
	}

	/**
	 * Collects all available category uids used by the currently displayed files.
	 * 
	 * @returns {Array}
	 */
	getAllowedCategories() {
		let mapAllowedCatUid = {};
		this.filePool.forEach(file => {
			let cats = file.getCategories();
			cats.forEach(cat => {
				if (!mapAllowedCatUid[cat.uid]) {
					mapAllowedCatUid[cat.uid] = true;

					if (cat.l10n_parent > 0) {
						mapAllowedCatUid[cat.l10n_parent] = true;
					}
				}
			});
		});

		return mapAllowedCatUid;
	}

	#collectFiles() {
		let files = this.container.querySelectorAll(this.selectors.fileItemSelector);
		files.forEach(file => {
			let fileUid = file.getAttribute('data-uid') || false; 
			if (fileUid !== false) {
				let _file = new File(file, this.data.files[ this.data.mapFileUidToIndex[fileUid] ]);
				this.files.push(_file);
			}
		});
	}

	sortFilesBy(key, direction) {
		this.filePool.sort((a, b) => {
			let keyA = a.data[key].toUpperCase().trim(); // Groß-/Kleinschreibung ignorieren
			let keyB = b.data[key].toUpperCase().trim(); // Groß-/Kleinschreibung ignorieren
			
			if (direction == 'desc') {
				if (keyA.localeCompare(keyB) > 0) {
					return -1;
				} else if (keyA.localeCompare(keyB) < 0) {
					return 1;
				} else {
					return 0;
				}
			} else {
				return keyA.localeCompare(keyB);
			}
		});
	}

	reorderDomFiles() {
		let ulContainer = this.filePool[0].getElement().parentNode;

		this.filePool.forEach(file => {
			let elem = file.getElement();
			ulContainer.appendChild(ulContainer.removeChild(elem));
		});

		this.#onChangePage(1);
	}
}
